// Multi-file uploader (AntD Upload.Dragger).
//
// ISSUE-61 — Uses the BE bulk endpoint (`POST /admin/files/upload-bulk`) so
// the whole batch goes up in a single multipart request. BE returns a
// `BulkUploadResultDto` envelope containing per-file successes AND per-file
// errors, which lets us surface a partial-success UI:
//   • progress bar reflects total bytes uploaded across the batch
//   • each queued row is reconciled with the BE response:
//     – matched in `items` → "Tamam" (or "Dedupe" if hashed-matched)
//     – matched in `errors` → red badge with BE code + message
//     – not matched (rare) → generic failure
// FE also enforces the BE limits client-side so we avoid round-tripping obviously-invalid
// batches. The per-file size + allowed extensions are admin-configurable (upload.maxFileSizeMb
// / upload.allowedExtensions, read via useUploadConstraints); the batch cap stays static.

import { useMemo, useState } from 'react';
import { Button, List, Progress, Tag, Typography, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { UploadCloud, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  filesApi,
  FILES_BATCH_LIMIT_BYTES,
  type BulkUploadResult,
} from '@api/files';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { useUploadConstraints } from '../hooks/useUploadConstraints';
import { isExtensionAllowed } from '../uploadConstraints';

const { Dragger } = Upload;
const { Text } = Typography;

export interface FileUploaderProps {
  /** Active folder id; uploaded files land in this folder (null = root). */
  folderId: string | null;
  onAllDone?: () => void;
}

type ItemStatus = 'queued' | 'uploading' | 'done' | 'dedupe' | 'error';

interface QueuedItem {
  uid: string;
  file: File;
  status: ItemStatus;
  errorCode?: string;
  errorMessage?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function FileUploader({ folderId, onAllDone }: FileUploaderProps) {
  const { t, i18n } = useTranslation(['media', 'errors']);
  const qc = useQueryClient();
  const { maxFileBytes, maxFileMb, allowedExtensions, accept } = useUploadConstraints();

  // Per-file BE failures arrive with a contract `code` + an English message.
  // Resolve the user-facing text from the code via the `errors` namespace
  // (never surface the raw BE message); fall back to the generic error copy.
  const td = t as unknown as (key: string) => string;
  const resolveBulkError = (code: string) => {
    const key = `errors:${code}`;
    return i18n.exists(key) ? td(key) : td('errors:default');
  };
  const [items, setItems] = useState<QueuedItem[]>([]);
  const [progress, setProgress] = useState(0);

  const bulkUpload = useMutation({
    mutationFn: async (vars: { files: File[] }) => {
      return filesApi.uploadBulk(vars.files, folderId, (p) => setProgress(p));
    },
  });

  /**
   * Reconcile the queued items with the BE bulk response. BE preserves the
   * input order in `items` and surfaces per-file failures in `errors[]`.
   */
  const reconcile = (queued: QueuedItem[], result: BulkUploadResult) => {
    // Build lookup maps by filename — BE keys errors by the original file name.
    const errorByName = new Map<string, BulkUploadResult['errors'][number]>();
    for (const err of result.errors) errorByName.set(err.fileName, err);
    const successByName = new Map<string, BulkUploadResult['items'][number]>();
    for (const it of result.items) successByName.set(it.fileName, it);

    setItems((prev) =>
      prev.map((it) => {
        if (!queued.find((q) => q.uid === it.uid)) return it;
        const err = errorByName.get(it.file.name);
        if (err) {
          return {
            ...it,
            status: 'error' as const,
            errorCode: err.code,
            errorMessage: resolveBulkError(err.code),
          };
        }
        const ok = successByName.get(it.file.name);
        if (ok) {
          return {
            ...it,
            status: ok.deduplicatedFromExisting ? ('dedupe' as const) : ('done' as const),
          };
        }
        return {
          ...it,
          status: 'error' as const,
          errorCode: 'UNKNOWN',
          errorMessage: t('uploader.error.noMatch'),
        };
      }),
    );
  };

  const runBatch = async (queued: QueuedItem[]) => {
    // Mark all as uploading.
    setItems((prev) =>
      prev.map((p) =>
        queued.find((q) => q.uid === p.uid) ? { ...p, status: 'uploading' } : p,
      ),
    );
    setProgress(0);
    try {
      const result = await bulkUpload.mutateAsync({ files: queued.map((q) => q.file) });
      reconcile(queued, result);
      if (result.errors.length > 0) {
        void message.warning(
          t('uploader.toast.partial', {
            uploaded: result.uploadedCount,
            failed: result.errors.length,
          }),
        );
      } else if (result.uploadedCount + result.deduplicatedCount > 0) {
        void message.success(
          result.deduplicatedCount > 0
            ? t('uploader.toast.successWithDedupe', {
                uploaded: result.uploadedCount,
                dedupe: result.deduplicatedCount,
              })
            : t('uploader.toast.success', { uploaded: result.uploadedCount }),
        );
      }
    } catch (err) {
      const msg = resolveApiErrorMessage(err);
      setItems((prev) =>
        prev.map((p) =>
          queued.find((q) => q.uid === p.uid)
            ? { ...p, status: 'error', errorCode: 'BATCH_FAILED', errorMessage: msg }
            : p,
        ),
      );
      void message.error(msg);
    } finally {
      setProgress(100);
      void qc.invalidateQueries({ queryKey: ['files'] });
      onAllDone?.();
    }
  };

  const draggerProps: UploadProps = {
    name: 'files',
    multiple: true,
    showUploadList: false,
    beforeUpload: (file, fileList) => {
      // Start the batch only when the LAST file in this drop arrives.
      if (file !== fileList[fileList.length - 1]) {
        return Upload.LIST_IGNORE;
      }
      // Client-side guard against the admin-configured per-file limits + the static batch cap.
      const totalBytes = fileList.reduce((s, f) => s + f.size, 0);
      if (totalBytes > FILES_BATCH_LIMIT_BYTES) {
        void message.error(
          t('uploader.toast.batchSizeExceeded', {
            total: formatBytes(totalBytes),
            limit: formatBytes(FILES_BATCH_LIMIT_BYTES),
          }),
        );
        return Upload.LIST_IGNORE;
      }
      // Per-file rejection reason (extension first, then size) — mirrors the BE handler gates.
      const rejectReason = (f: File): { code: string; message: string } | null => {
        if (!isExtensionAllowed(f.name, allowedExtensions)) {
          return {
            code: 'FILE_TYPE_NOT_ALLOWED',
            message: t('uploader.error.fileTypeNotAllowed', {
              types: allowedExtensions.join(', '),
            }),
          };
        }
        if (f.size > maxFileBytes) {
          return {
            code: 'FILE_TOO_LARGE',
            message: t('uploader.error.fileTooLarge', { limit: formatBytes(maxFileBytes) }),
          };
        }
        return null;
      };
      const queued: QueuedItem[] = fileList.map((f) => ({
        uid: f.uid || `${Date.now()}-${Math.random()}`,
        file: f,
        status: 'queued',
      }));
      const acceptable: QueuedItem[] = [];
      const rejected: QueuedItem[] = [];
      for (const q of queued) {
        const reason = rejectReason(q.file);
        if (reason) {
          rejected.push({ ...q, status: 'error', errorCode: reason.code, errorMessage: reason.message });
        } else {
          acceptable.push(q);
        }
      }
      setItems([...rejected, ...acceptable]);
      if (acceptable.length === 0) {
        void message.error(t('uploader.toast.noneUploaded'));
        return Upload.LIST_IGNORE;
      }
      void runBatch(acceptable);
      return Upload.LIST_IGNORE; // we manage the list ourselves
    },
    accept: accept || undefined,
  };

  const clearDone = () =>
    setItems((prev) => prev.filter((p) => p.status !== 'done' && p.status !== 'dedupe'));

  const summary = useMemo(() => {
    const done = items.filter((i) => i.status === 'done').length;
    const dedupe = items.filter((i) => i.status === 'dedupe').length;
    const failed = items.filter((i) => i.status === 'error').length;
    const inFlight = items.filter((i) => i.status === 'uploading' || i.status === 'queued').length;
    return { done, dedupe, failed, inFlight };
  }, [items]);

  return (
    <div data-testid="file-uploader">
      <Dragger {...draggerProps}>
        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
          <UploadCloud size={32} />
        </p>
        <p className="ant-upload-text" style={{ fontWeight: 500 }}>
          {t('uploader.dragText')}
        </p>
        <p className="ant-upload-hint" style={{ fontSize: 12 }}>
          {t('uploader.dragHint', {
            perFile: `${maxFileMb} MB`,
            batch: formatBytes(FILES_BATCH_LIMIT_BYTES),
          })}
        </p>
        {allowedExtensions.length > 0 ? (
          <p className="ant-upload-hint" style={{ fontSize: 11, opacity: 0.75 }}>
            {t('uploader.allowedTypes', { types: allowedExtensions.join(', ') })}
          </p>
        ) : null}
      </Dragger>

      {items.length > 0 ? (
        <div style={{ marginTop: 12 }} data-testid="file-uploader-queue">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 6,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Text strong>{t('uploader.queueTitle', { count: items.length })}</Text>
            <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
              {summary.done > 0 ? (
                <Tag color="success">{t('uploader.summary.uploaded', { count: summary.done })}</Tag>
              ) : null}
              {summary.dedupe > 0 ? (
                <Tag color="blue">{t('uploader.summary.dedupe', { count: summary.dedupe })}</Tag>
              ) : null}
              {summary.failed > 0 ? (
                <Tag color="error" data-testid="file-uploader-failed-tag">
                  {t('uploader.summary.failed', { count: summary.failed })}
                </Tag>
              ) : null}
              {(summary.done > 0 || summary.dedupe > 0) ? (
                <Button size="small" type="link" onClick={clearDone}>
                  {t('uploader.clearDone')}
                </Button>
              ) : null}
            </div>
          </div>

          {bulkUpload.isPending || (progress > 0 && progress < 100) ? (
            <Progress
              percent={progress}
              status="active"
              size="small"
              style={{ marginBottom: 8 }}
              data-testid="file-uploader-progress"
            />
          ) : null}

          <List
            size="small"
            dataSource={items}
            renderItem={(it) => (
              <List.Item
                key={it.uid}
                actions={[
                  it.status === 'error' ? (
                    <Button
                      key="remove"
                      type="text"
                      size="small"
                      icon={<X size={12} />}
                      onClick={() =>
                        setItems((prev) => prev.filter((p) => p.uid !== it.uid))
                      }
                      aria-label={t('uploader.removeFromQueue')}
                    />
                  ) : null,
                ]}
              >
                <div style={{ width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 12,
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '55%',
                      }}
                    >
                      {it.file.name}{' '}
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        · {formatBytes(it.file.size)}
                      </Text>
                    </span>
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      {it.status === 'queued' ? (
                        <Tag color="default">{t('uploader.status.queued')}</Tag>
                      ) : it.status === 'uploading' ? (
                        <Tag color="processing">{t('uploader.status.uploading')}</Tag>
                      ) : it.status === 'done' ? (
                        <Tag color="success">{t('uploader.status.done')}</Tag>
                      ) : it.status === 'dedupe' ? (
                        <Tag color="blue">{t('uploader.status.dedupe')}</Tag>
                      ) : (
                        <Tag
                          color="error"
                          data-testid="file-uploader-error-tag"
                          title={it.errorMessage}
                        >
                          {it.errorCode ?? t('uploader.status.error')}
                        </Tag>
                      )}
                    </span>
                  </div>
                  {it.status === 'error' && it.errorMessage ? (
                    <Text
                      type="danger"
                      style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                    >
                      {it.errorMessage}
                    </Text>
                  ) : null}
                </div>
              </List.Item>
            )}
          />
        </div>
      ) : null}
    </div>
  );
}
